import type { Project } from '../core/schema';
import { renderComposition } from './compositionRenderer';
import { audioEngine } from './audioEngine';

export interface ExportOptions {
  width: number;
  height: number;
  fps: number;
  videoBitsPerSecond: number;
  range: [number, number];
  onProgress?(progress: number): void;
  signal?: AbortSignal;
}

function supportedMimeType() {
  const candidates = ['video/webm;codecs=vp9,opus', 'video/webm;codecs=vp8,opus', 'video/webm'];
  return candidates.find((type) => MediaRecorder.isTypeSupported(type)) ?? '';
}

export async function exportProjectVideo(project: Project, options: ExportOptions): Promise<Blob> {
  if (!('MediaRecorder' in window) || !HTMLCanvasElement.prototype.captureStream) throw new Error('Video export is unavailable in this browser. Try a recent Chrome, Edge, or Firefox build.');
  const composition = project.compositions[project.activeCompositionId];
  const canvas = document.createElement('canvas');
  canvas.width = options.width;
  canvas.height = options.height;
  const quality = options.width / composition.width;
  const videoStream = canvas.captureStream(options.fps);
  const audioStream = await audioEngine.play(project, options.range[0], true);
  const tracks = [...videoStream.getVideoTracks(), ...(audioStream?.getAudioTracks() ?? [])];
  const stream = new MediaStream(tracks);
  const recorder = new MediaRecorder(stream, { mimeType: supportedMimeType(), videoBitsPerSecond: options.videoBitsPerSecond });
  const chunks: Blob[] = [];
  recorder.ondataavailable = (event) => { if (event.data.size) chunks.push(event.data); };
  const stopped = new Promise<void>((resolve, reject) => { recorder.onstop = () => resolve(); recorder.onerror = (event) => reject(new Error(`MediaRecorder failed: ${event.type}`)); });
  recorder.start(1000);
  const start = performance.now();
  const duration = options.range[1] - options.range[0];
  let time = options.range[0];
  while (time < options.range[1]) {
    if (options.signal?.aborted) { recorder.stop(); audioEngine.stop(); throw new DOMException('Export cancelled', 'AbortError'); }
    const elapsed = (performance.now() - start) / 1000;
    time = Math.min(options.range[1], options.range[0] + elapsed);
    await renderComposition(canvas, project, time, true, quality);
    options.onProgress?.(Math.min(1, (time - options.range[0]) / duration));
    await new Promise((resolve) => requestAnimationFrame(resolve));
  }
  recorder.stop();
  await stopped;
  audioEngine.stop();
  stream.getTracks().forEach((track) => track.stop());
  return new Blob(chunks, { type: recorder.mimeType || 'video/webm' });
}
